import React, { FC, useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { useMenuContext } from 'state/menu';
import { useUIContext } from 'state/ui';

import { ModifierListItem } from './modifier-list-item';
import { IModifierTypeUIProps, IRadioCheckListOption } from './types';
import {
  getModifierOptionMultiplier,
  getSingleSelectedOption,
  logProductModifierCustomization,
  transformSelectedOptionToSelections,
} from './utils';

export const ModifierCheckbox: FC<IModifierTypeUIProps> = ({
  modifier,
  selections,
  onSelectionsChange,
  selectedItem,
}) => {
  const { formatCurrencyForLocale } = useUIContext();
  const { formatMessage } = useIntl();
  const { priceForItemOptionModifier } = useMenuContext();

  const selectedOption = useMemo(() => getSingleSelectedOption(selections), [selections]);

  const isSelected = getModifierOptionMultiplier(selectedOption) === 1;

  const handleCheckPress = useCallback(
    (optionKey: string) => {
      const newSelectedOption = modifier.options.find(opt => opt._key === optionKey);

      if (newSelectedOption) {
        onSelectionsChange(transformSelectedOptionToSelections(newSelectedOption));

        logProductModifierCustomization(
          newSelectedOption.name?.locale ?? '',
          modifier.name.locale,
          newSelectedOption._key
        );
      }
    },
    [modifier.options, modifier.name.locale, onSelectionsChange]
  );

  const badgeLabelAdded = formatMessage({ id: 'regular' });

  const checkBoxOptions: IRadioCheckListOption[] = useMemo(() => {
    // Sort the options by the multiplier, going from lowest to highest.
    return (modifier.options || [])
      .sort(
        (a, b) => (a.modifierMultiplier?.multiplier ?? 0) - (b.modifierMultiplier?.multiplier ?? 0)
      )
      .map(opt => {
        const isRegular = opt.modifierMultiplier?.multiplier !== 0;

        const price = priceForItemOptionModifier({
          item: selectedItem,
          itemOption: modifier,
          modifier: opt,
        });

        const label = formatMessage({
          id: isRegular ? 'regular' : 'no',
        });

        return {
          label,
          labelSuffix: price > 0 ? `+${formatCurrencyForLocale(price)}` : '',
          value: opt._key,
        };
      });
  }, [formatCurrencyForLocale, formatMessage, modifier, priceForItemOptionModifier, selectedItem]);

  return (
    <ModifierListItem
      badgeContent={isSelected ? badgeLabelAdded : ''}
      content={modifier}
      selectedOption={selectedOption}
      options={checkBoxOptions}
      onOptionPress={handleCheckPress}
      isSelected={isSelected}
    />
  );
};
